import CryptoJS from "crypto-js";

import { setSelected } from "../../../../slices/collections/collectionsSlice";
import {
  fetchSharepointCollections,
  fetchSharepointContent,
  fetchSharepointSites,
} from "../../../../slices/sharepoint/actions";
import { uploadChunk } from "../../../../slices/workspace/actions/uploadFilesHelpers";
import { getCollectionName } from "../utils";

export const CHUNK_SIZE = 1024 * 1024 * 5; // 5MB chunks

export const calculateFileHash = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof File)) {
      reject(new Error("Invalid file: The file is empty or not a File object"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const binary = event.target.result;
      const wordArray = CryptoJS.lib.WordArray.create(binary);

      const hash = CryptoJS.SHA256(wordArray);
      resolve(hash.toString());
    };
    reader.onerror = (error) => {
      reject(new Error(`Error reading file: ${error.message}`));
    };
    reader.readAsArrayBuffer(file);
  });
};
export const calculateChunkMetadata = (file) => {
  const totalChunks = Math.ceil(file.content.size / CHUNK_SIZE);
  const chunks = [];

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.content.size);

    chunks.push({
      index: i,
      start,
      end,
    });
  }
  return { totalChunks, chunks };
};

export const uploadFiles = async (files) => {
  for (const file of files) {
    const { chunks } = calculateChunkMetadata(file);
    for (const chunk of chunks) {
      const start = chunk.start;
      const end = chunk.end;
      const chunkBlob = file.content.slice(start, end);

      await uploadChunk(file, chunkBlob, chunk.index);
    }
  }
};
export const getIndexStatus = (file, updatedState) => {
  if (updatedState === undefined) {
    return file.indexedState;
  }

  return updatedState.hasToBeIndexed || updatedState.hasToBeUploaded ? 2 : 0;
};

export const fetchSharepointData = (dispatch, item) => {
  try {
    if (!item?.type) {
      dispatch(fetchSharepointSites());
    } else if (item.type === "site") {
      dispatch(fetchSharepointCollections(item));
    } else if (item.type === "collection" || item.type === "folder") {
      dispatch(fetchSharepointContent(item));
    }
  } catch {}
};

export const collectionsSelectHelper = ({
  selectedCollection,
  selectedSource,
  email,
  dispatch,
  collections,
}) => {
  const ensureCollectionHasName = () => {
    const collectionToUpdate = selectedCollection || {
      name: selectedCollection?.name
        ? selectedCollection.name
        : getCollectionName(collections),
      files: [],
    };

    if (!selectedCollection) {
      dispatch(setSelected(collectionToUpdate));
    }

    return collectionToUpdate;
  };

  const createFileSet = (files) => {
    return new Set(files.map((file) => [file.content.name, file.source]));
  };

  const generateFileHashes = async (files) => {
    return Promise.all(
      files.map(async (file) => ({
        file,
        hash:
          selectedSource.key === "sharepoint"
            ? file.hash
            : await calculateFileHash(file),
      }))
    );
  };

  const filterUniqueFiles = (filesWithHashes, existingFilesSet) => {
    return filesWithHashes.filter(({ file }) => {
      const fileSource = file.source || "Local Files";
      return ![...existingFilesSet].some(
        ([name, source]) => name === file.name && source === fileSource
      );
    });
  };

  const formatNewFiles = (filesWithHashes) => {
    return filesWithHashes.map(({ file, hash }) => ({
      id: null,
      hash,
      content: file,
      source: selectedSource.label,
      indexedState: 2,
      owner: email,
    }));
  };

  return {
    ensureCollectionHasName,
    createFileSet,
    generateFileHashes,
    filterUniqueFiles,
    formatNewFiles,
  };
};

export const createSharepointNavigationPath = (dispatch, row, setPath) => {
  const sitePaths = row.sitePath.split("/");
  const siteName = sitePaths[sitePaths.length - 1];
  const sitePathString = sitePaths.join("/");

  const newPath = [
    { name: "Sites" },
    {
      name: siteName,
      type: "site",
      sitePath: sitePathString,
      host: row.host,
      accessStatus: row.type === "site" ? row.accessStatus : undefined,
    },
  ];

  if (row.type === "folder" || row.type === "collection") {
    const filePath = row.filePath.split("/");

    filePath.slice(1).forEach((fileName, i) => {
      newPath.push({
        name: fileName,
        type: i === 0 ? "collection" : "folder",
        sitePath: sitePathString,
        filePath: filePath.slice(0, i + 2).join("/"),
        host: row.host,
      });
    });
  }

  dispatch(setPath(newPath));
};
